import { Globe, MapPin } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  ACTIONS,
  ACTION_LABELS_RU,
  RESOURCE_CATALOG,
  applyAllPreset,
  fieldForAction,
  toggleCell,
  toggleScopeBound,
  type Action,
  type ChangedCells,
  type MatrixState,
} from '@/pages/company/roles/rolesLogic';

interface PermissionMatrixProps {
  matrix: MatrixState;
  /** null = read-only (системная роль), иначе — сеттер изменённой матрицы. */
  onChange?: ((next: MatrixState) => void) | null;
  /** Подсветить изменённые ячейки (диф-превью). */
  changed?: ChangedCells;
}

const ACTION_HEAD: Record<Action, string> = {
  C: 'Создание',
  R: 'Просмотр',
  U: 'Изменение',
  D: 'Удаление',
};

/**
 * PermissionMatrix — таблица «11 ресурсов × C/R/U/D» (AK-343, ADR-040).
 *
 * Режимы:
 *   - редактируемый (`onChange` задан): чекбоксы кликабельны, строчные и
 *     общие пресеты, переключатель scope_bound «зона/компания»;
 *   - read-only (`onChange` = null/undefined): системная роль — чекбоксы
 *     задизейблены, пресеты и scope-toggle скрыты;
 *   - диф (`changed`): изменённые ячейки в амбер-обводке (accent-brand).
 *
 * Адаптив: таблица в overflow-x на узких вьюпортах (min-w), без 100vw.
 * DS: только @/components/ui/*, semantic-классы, ноль hex.
 */
export function PermissionMatrix({ matrix, onChange, changed }: PermissionMatrixProps) {
  const editable = !!onChange;

  return (
    <TooltipProvider delayDuration={200}>
      <div className="overflow-hidden rounded-lg border border-border">
        <div className="border-b border-border bg-secondary/50 px-3 py-2 text-[11.5px] leading-relaxed text-muted-foreground">
          <b className="text-foreground">C</b> — создание · <b className="text-foreground">R</b> —
          просмотр · <b className="text-foreground">U</b> — изменение ·{' '}
          <b className="text-foreground">D</b> — удаление. Отметьте, что роль может делать с каждым
          разделом.
        </div>

        {editable && (
          <div className="flex flex-wrap items-center gap-2 border-b border-border/70 px-3 py-2 text-xs text-muted-foreground">
            <span>Быстро для всех:</span>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => onChange!(applyAllPreset(matrix, 'full'))}
            >
              Полный доступ
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => onChange!(applyAllPreset(matrix, 'read'))}
            >
              Только просмотр
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2.5 text-xs"
              onClick={() => onChange!(applyAllPreset(matrix, 'none'))}
            >
              Снять всё
            </Button>
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr>
                <th className="border-b border-border px-3 py-2 text-left text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
                  Раздел / ресурс
                </th>
                {ACTIONS.map((a) => (
                  <th
                    key={a}
                    className="border-b border-border px-1.5 py-2 text-center text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground"
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="cursor-default">{a}</span>
                      </TooltipTrigger>
                      <TooltipContent>{ACTION_HEAD[a]}</TooltipContent>
                    </Tooltip>
                  </th>
                ))}
                <th className="border-b border-border px-2 py-2 text-center text-[10.5px] font-bold uppercase tracking-wide text-muted-foreground">
                  {editable ? 'Зона' : ''}
                </th>
              </tr>
            </thead>
            <tbody>
              {RESOURCE_CATALOG.map((res) => {
                const row = matrix[res.code];
                const rowChanged = changed?.[res.code];
                return (
                  <tr key={res.code} className="border-b border-border/60 last:border-b-0">
                    <td className="px-3 py-2.5 align-middle">
                      <span className="block font-medium text-foreground">{res.name}</span>
                      <span className="block text-[11px] text-muted-foreground">{res.hint}</span>
                    </td>
                    {ACTIONS.map((a) => {
                      const field = fieldForAction(a);
                      const on = row?.[field] ?? false;
                      const isChanged = rowChanged?.[a] ?? false;
                      return (
                        <td key={a} className="px-1.5 py-2.5 text-center align-middle">
                          <span
                            className={
                              isChanged
                                ? 'inline-flex rounded-md p-0.5 ring-2 ring-accent-brand/50'
                                : 'inline-flex p-0.5'
                            }
                          >
                            <Checkbox
                              checked={on}
                              disabled={!editable}
                              aria-label={`${res.name}: ${ACTION_LABELS_RU[a]}`}
                              onCheckedChange={() =>
                                editable && onChange!(toggleCell(matrix, res.code, a))
                              }
                            />
                          </span>
                        </td>
                      );
                    })}
                    <td className="px-2 py-2.5 text-center align-middle">
                      {editable ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type="button"
                              onClick={() => onChange!(toggleScopeBound(matrix, res.code))}
                              className="inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-secondary"
                            >
                              {row?.scope_bound ? (
                                <MapPin className="h-4 w-4" />
                              ) : (
                                <Globe className="h-4 w-4" />
                              )}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {row?.scope_bound
                              ? 'В своей зоне — нажмите, чтобы дать по всей компании'
                              : 'По всей компании — нажмите, чтобы ограничить своей зоной'}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        <span className="text-muted-foreground">
                          {row?.scope_bound ? (
                            <MapPin className="mx-auto h-4 w-4" />
                          ) : (
                            <Globe className="mx-auto h-4 w-4" />
                          )}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </TooltipProvider>
  );
}

/** Компактная лег для scope-иконок. */
export function ScopeLegend() {
  return (
    <div className="flex flex-wrap gap-4 text-[11px] text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <MapPin className="h-3.5 w-3.5" /> в своей зоне
      </span>
      <span className="inline-flex items-center gap-1.5">
        <Globe className="h-3.5 w-3.5" /> по всей компании
      </span>
    </div>
  );
}
